import AgentSessionReadyToast from '@/components/Agents/AgentSessionReadyToast/AgentSessionReadyToast';
import { agentUrlSlugMap, isAgentEnabled } from '@/components/Agents/AgentUtils';
import webRoutes from '@/constants/webRoutes.json';
import { errorToast, successToast, warningToast } from '@/helpers.tsx';
import { _notNil } from '@/littledash';
import { type AgentSessionSettledEventV1 } from '@/model/TeamEvent.model';
import { featuresSelector } from '@/support/Selectors';
import { web as webRoute } from '@/support/route';
import { useTeamEvent } from '@/support/teamEvents/useTeamEvent';
import { type FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { type Id, toast } from 'react-toastify';

const SESSION_ROUTE = webRoutes['agents.session'].replace(/\{(\w+)\}/g, ':$1');

const viewedSessionApiId = (pathname: string): string | undefined =>
  matchPath(SESSION_ROUTE, pathname)?.params.sessionApiId;

const toastFor = (status: AgentSessionSettledEventV1['status']) => {
  switch (status) {
    case 'failed':
    case 'cancelled':
      return errorToast;
    case 'awaiting_input':
      return warningToast;
    default:
      return successToast;
  }
};

/**
 * Raises a toast when an agent run the user started settles — ready, waiting on them, failed, or
 * stopped by the system — so they hear about it wherever they are in the app. One toast per
 * session: a later event replaces the earlier one, and reaching the session page any way closes it.
 *
 * Shown only where the agent is enabled (`ai_agents` and the agent's own flag). The events arrive
 * on the team event stream opened by `TeamEventProvider`.
 */
const AgentSessionSettledToastListener: FC = () => {
  const features = useSelector(featuresSelector);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toastBySession = useRef(new Map<string, Id>());

  useEffect(() => {
    const viewed = viewedSessionApiId(pathname);
    if (_notNil(viewed) && toastBySession.current.has(viewed)) {
      toast.dismiss(toastBySession.current.get(viewed));
      toastBySession.current.delete(viewed);
    }
  }, [pathname]);

  useTeamEvent('agent_session_settled', (event) => {
    if (!isAgentEnabled(features, event.agent_type)) {
      return;
    }
    if (viewedSessionApiId(pathname) === event.session_api_id) {
      return;
    }
    const previous = toastBySession.current.get(event.session_api_id);
    if (_notNil(previous)) {
      toast.dismiss(previous);
    }
    const id = toastFor(event.status)(
      ({ closeToast }) => (
        <AgentSessionReadyToast
          event={event}
          onOpen={() => {
            navigate(
              webRoute('agents.session', {
                slug: agentUrlSlugMap[event.agent_type],
                sessionApiId: event.session_api_id,
              })
            );
            closeToast();
          }}
        />
      ),
      { toastId: event.id, closeOnClick: false, autoClose: 15_000 }
    );
    toastBySession.current.set(event.session_api_id, id);
  });

  return null;
};

export default AgentSessionSettledToastListener;
